import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, CheckCircle2, ClipboardList, Calendar, Phone, Target, Cog, Shield, Check } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/layout/Breadcrumbs';
import { getPaths } from '../utils/paths';

export default function Werkwijze() {
  const { language, t } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';
  const paths = getPaths(language);

  const steps = [
    {
      step: '01',
      title: language === 'nl' ? 'Vrijblijvende Kennismaking' : 'Free Introduction',
      description: language === 'nl'
        ? 'Tijdens een digitale kennismaking bespreken we de business, groeiambities en de huidige uitdagingen. We inventariseren de beschikbare resources binnen jouw team\u2014zoals developers of content writers\u2014zodat de koers die we varen aansluit op de interne uitvoeringcapaciteit.'
        : 'During a digital introduction, we discuss the business, growth ambitions, and current challenges. We inventory the available resources within your team\u2014such as developers or content writers\u2014so that the course we set aligns with internal execution capacity.',
      icon: <Phone className="h-5 w-5" />
    },
    {
      step: '02',
      title: language === 'nl' ? 'Voorstel op Maat' : 'Bespoke Proposal',
      description: language === 'nl'
        ? 'Op basis van een analyse van de website, concurrenten en bedrijfsdoelen, breng ik de groeikansen in kaart. Je ontvangt een advies over de aanpak die nodig is om resultaat te boeken, rekening houdend met het budget en de aanwezige expertise in huis. Voorstellen zijn gebaseerd op strategische sprints van 3 maanden, die per maand worden gefactureerd.'
        : 'Based on an analysis of the website, competitors, and business goals, I map out the growth opportunities. You receive advice on the approach needed to achieve results, taking into account the budget and existing in-house expertise. Proposals are based on strategic 3-month sprints, billed monthly.',
      icon: <Target className="h-5 w-5" />
    },
    {
      step: '03',
      title: language === 'nl' ? 'Strategisch Plan' : 'Strategic Plan',
      description: language === 'nl'
        ? 'Zodra de koers is bepaald, ontwikkel ik een diepgaande roadmap die de basis vormt voor alle acties. Ik focus hierbij op de drie SEO-pijlers: Technical Excellence, een Content-strategie die aansluit op de customer journey, en het bouwen van Autoriteit. Daarnaast neem ik gericht advies op voor LLM-zichtbaarheid binnen AI-gedreven zoekresultaten. Dit plan zorgt dat alle betrokkenen weten wat de prioriteiten zijn.'
        : 'Once the course is set, I develop an in-depth roadmap that forms the basis for all actions. I focus on the three SEO pillars: Technical Excellence, a Content strategy aligned with the customer journey, and building Authority. Additionally, I include targeted advice for LLM visibility within AI-driven search results. This plan ensures all stakeholders know the priorities.',
      icon: <ClipboardList className="h-5 w-5" />
    },
    {
      step: '04',
      title: language === 'nl' ? 'SEO Executiefase' : 'SEO Execution Phase',
      description: language === 'nl'
        ? 'In deze fase komt de strategie tot leven. We werken in sprints van 3 maanden om momentum op te bouwen. Ik stuur aan op strategisch niveau en werk samen met het in-house team of externe partners. Via een centraal werkdocument is er inzicht in alle actiepunten, de status van taken en de resultaten.'
        : 'In this phase, the strategy comes to life. We work in 3-month sprints to build momentum. I lead at a strategic level and work with the in-house team or external partners. Via a central work document, there is insight into all action items, task status, and results.',
      icon: <Cog className="h-5 w-5" />
    },
    {
      step: '05',
      title: language === 'nl' ? 'Onderhoudsfase' : 'Maintenance Phase',
      description: language === 'nl'
        ? 'Zodra de fundamenten staan, borgen we het resultaat. We blijven de data monitoren en sturen bij op basis van algoritme-updates en nieuwe kansen in de markt. Op basis van de benodigde werkzaamheden bepalen we samen de koers voor de onderhoudsfase om de organische positie te behouden en uit te bouwen.'
        : 'Once the foundations are in place, we secure the results. We continue to monitor data and adjust based on algorithm updates and new market opportunities. Based on the required work, we determine the course for the maintenance phase together to maintain and expand the organic position.',
      icon: <Shield className="h-5 w-5" />
    }
  ];

  const documentColumns = [
    { name: 'Omschrijving', en: 'Description' },
    { name: 'Pijler', en: 'Pillar' },
    { name: 'Categorie', en: 'Category' },
    { name: 'Context', en: 'Context' },
    { name: 'Status', en: 'Status' },
  ];

  return (
    <div data-testid="werkwijze-page">
      <SEOHead
        title={language === 'nl' ? 'Werkwijze | Zo werkt de samenwerking met Kesult' : 'How I work | A structured approach to your Dutch SEO success'}
        description={language === 'nl'
          ? 'Van kennismaking tot onderhoud: mijn werkwijze in 5 stappen. Strategische SEO-sprints van 3 maanden, transparant via een centraal werkdocument.'
          : 'From introduction to maintenance: my process in 5 steps. Strategic 3-month SEO sprints, transparent via a central work document. See how we collaborate.'}
        canonical="https://www.kesult.com/werkwijze"
        lang={language}
      />
      {/* Hero - Dark angled section */}
      <section className="relative bg-[#0B1A2E] text-white overflow-hidden" data-testid="werkwijze-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-32 lg:pb-40 relative z-10">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-5">
              {language === 'nl' ? 'Werkwijze' : 'Process'}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-tight mb-6" data-testid="werkwijze-title">
              {language === 'nl' 
                ? 'In 5 stappen naar organische groei' 
                : 'In 5 steps to organic growth'}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
              {language === 'nl'
                ? 'Mijn werkwijze is gestoeld op data, transparantie en een strakke regie. Zo transformeren we jouw ambities naar meetbaar resultaat.'
                : 'My approach is built on data, transparency, and tight control. This is how we transform your ambitions into measurable results.'}
            </p>

            <ul className="space-y-3 mb-10">
              {(language === 'nl' 
                ? ['Strategische sprints van 3 maanden', 'Inzicht via een werkdocument in alle actiepunten']
                : ['Strategic 3-month sprints', 'Insight via a work document into all action items']
              ).map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-white/80">
                  <Check className="h-4 w-4 text-[#4286F4] flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`${basePath}/contact`}>
                <button className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center gap-2" data-testid="werkwijze-cta-primary">
                  {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to={paths.werkwijze}>
                <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center gap-2" data-testid="werkwijze-cta-secondary">
                  {language === 'nl' ? 'Bekijk werkwijze' : 'View process'}
                </button>
              </Link>
            </div>
          </div>
        </div>
        {/* Angled bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      <Breadcrumbs />

      {/* 5-Step Process */}
      <section className="py-20 lg:py-28 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Het proces' : 'The Process'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-14">
            {language === 'nl' ? 'Helder. Geen poespas.' : 'Clear. No fluff.'}
          </h2>

          <div className="space-y-6">
            {steps.map((step, index) => (
              <div
                key={index}
                className="group grid grid-cols-1 lg:grid-cols-[80px_1fr] gap-6 lg:gap-10 p-8 lg:p-10 rounded-2xl border border-gray-100 hover:border-[#4286F4]/30 hover:shadow-lg transition-all duration-300"
                data-testid={`step-${step.step}`}
              >
                <div className="flex-shrink-0">
                  <span className="text-5xl font-bold text-[#4286F4]">{step.step}</span>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 bg-[#4286F4]/10 rounded-lg flex items-center justify-center text-[#4286F4]">
                      {step.icon}
                    </div>
                    <h3 className="text-xl font-bold text-[#08080A]">{step.title}</h3>
                  </div>
                  <p className="text-[#555] leading-relaxed max-w-3xl">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Master Document */}
      <section className="py-20 lg:py-28 bg-[#08080A] text-white" data-testid="document-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-4">
                {language === 'nl' ? 'Transparantie' : 'Transparency'}
              </p>
              <h2 className="text-3xl font-bold mb-6">
                {language === 'nl' ? 'Het werkdocument' : 'The Work Document'}
              </h2>
              <p className="text-[#A1A1AA] leading-relaxed mb-8">
                {language === 'nl'
                  ? 'Alle taken, prioriteiten en voortgang worden beheerd in een centraal werkdocument. Zo heb je altijd inzicht in alle actiepunten.'
                  : 'All tasks, priorities, and progress are managed in a central work document. This way you always have insight into all action items.'}
              </p>
              <div className="space-y-4">
                {[
                  { icon: <ClipboardList className="h-5 w-5" />, text: language === 'nl' ? 'Inzicht in alle actiepunten' : 'Insight into all action items' },
                  { icon: <CheckCircle2 className="h-5 w-5" />, text: language === 'nl' ? 'Duidelijke prioriteiten en status' : 'Clear priorities and status' },
                  { icon: <Calendar className="h-5 w-5" />, text: language === 'nl' ? 'Volledige transparantie over voortgang' : 'Full transparency on progress' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="text-[#4286F4]">{item.icon}</div>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white text-black rounded-2xl overflow-hidden border-2 border-[#4286F4] shadow-2xl">
              <div className="bg-[#4286F4] text-white px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-white/30" />
                  <div className="w-3 h-3 rounded-full bg-white/30" />
                  <div className="w-3 h-3 rounded-full bg-white/30" />
                </div>
                <span className="text-sm ml-2 font-semibold">Werkdocument</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F1F0EF] border-b">
                    <tr>
                      {documentColumns.map((col, i) => (
                        <th key={i} className="px-3 py-2 text-left font-semibold uppercase tracking-wide">
                          {language === 'nl' ? col.name : col.en}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { desc: 'Fix Core Web Vitals', pillar: 'Tech', cat: 'Performance', ctx: 'LCP > 2.5s', status: 'In Progress' },
                      { desc: 'Optimize meta titles', pillar: 'Content', cat: 'On-page', ctx: '45 pages', status: 'Done' },
                      { desc: 'Build backlinks', pillar: 'Authority', cat: 'Linkbuilding', ctx: 'DR 30+', status: 'Planned' }
                    ].map((row, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-3 py-2">{row.desc}</td>
                        <td className="px-3 py-2"><span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-[#4286F4] text-white rounded">{row.pillar}</span></td>
                        <td className="px-3 py-2">{row.cat}</td>
                        <td className="px-3 py-2 text-[#71717A]">{row.ctx}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                            row.status === 'Done' ? 'bg-[#4286F4] text-white' : row.status === 'In Progress' ? 'bg-[#F59E0B] text-white' : 'bg-[#E4E4E7] text-black'
                          }`}>{row.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#4286F4] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {language === 'nl' ? 'Klaar voor een SEO-strategie op maat?' : 'Ready for a bespoke SEO strategy?'}
          </h2>
          <div className="flex gap-4">
            <Link to={`${basePath}/contact`}>
              <button className="bg-white text-[#4286F4] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
                {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <a href="mailto:info@kesult.com">
              <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors">
                {language === 'nl' ? 'Mail me' : 'Email me'}
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
